

import { useState } from 'react';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import authService from '../../../services/api/authService';
import Toast from "../../../components/Toast/Toast";
import { useAppDispatch } from '../../../store';
import { resetAuth } from '../../../store/reducers/authSlice';
import { getPasswordErrors } from '../../../utils/validation';
interface ResetPasswordProps {
  onContinue: () => void;
  onBack: () => void;
}
export default function ResetPassword({
  onBack,
}: ResetPasswordProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const passwordErrors = getPasswordErrors(newPassword);

  const [error] = useState('');
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "warning";
  } | null>(null);

  const showToast = (
    message: string,
    type: "success" | "error" | "warning"
  ) => {
    setToast({ message, type });
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!oldPassword || !newPassword || !confirmPassword) {
      showToast("All fields are required", "error");
      return;
    }

    if (newPassword !== confirmPassword) {
      showToast("New passwords do not match", "error");
      return;
    }

    if (oldPassword === newPassword) {
      showToast("New password must be different from old password", "error");
      return;
    }
    if (passwordErrors.length > 0) {
      showToast("Please meet the password requirements", "error");
      return;
    }
    try {
      await authService.changePassword(oldPassword, newPassword);

      showToast("Password changed successfully! Please login with your new password.", "success");

      setTimeout(async () => {
        await authService.logout();
        dispatch(resetAuth());
        navigate("/", { replace: true });
      }, 2000);

    } catch (error: any) {
      let errorMessage = "Something went wrong. Please try again.";

      if (error.code === 'auth/wrong-password'||'auth/invalid-credential') {
        errorMessage = "Old password is incorrect.";
      } else if (error.code === 'auth/weak-password') {
        errorMessage = "New password is too weak. Please use a stronger password.";
      } else if (error.code === 'auth/requires-recent-login') {
        errorMessage = "Please log in again before changing your password.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      showToast(errorMessage, "error");
    }
  };


  return (
    <div className="h-screen bg-[#F5F5F5] flex flex-col items-center px-6 pt-4 pb-12 relative">
      <div className="w-full max-w-xl flex flex-col items-center flex-1">
        <button onClick={onBack} className="absolute left-6 top-4 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center z-10" aria-label="Go Back">
          <ArrowLeft className="w-6 h-6 text-brand-primary" />
        </button>

        <h1 className="text-[26px] md:text-3xl lg:text-4xl font-semibold tracking-wide text-center mb-4 mt-12">
          Change Password
        </h1>

        <p className="text-base md:text-lg text-[#757575] text-center mb-6">
          Enter and confirm your new password to regain access
        </p>

        {error && (
          <div className="w-full mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600 text-center">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="w-full max-w-xl flex flex-col flex-1">
          <div className="space-y-6">
            <div>
              <label className="block text-base md:text-lg text-[#757575] mb-2">
                Old Password
              </label>
              <div className="relative">
                <input
                  type={showOld ? 'text' : 'password'}
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  className="w-full h-12 md:h-14 px-4 pr-12 text-base md:text-lg border-2 border-gray-300 rounded-lg bg-white focus:outline-none focus:border-brand-primary"
                />
                <button
                  type="button"
                  onClick={() => setShowOld(!showOld)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 min-w-[44px] min-h-[44px] flex items-center justify-center"
                >
                  {showOld ? (
                    <EyeOff className="w-5 h-5 text-[#757575]" />
                  ) : (
                    <Eye className="w-5 h-5 text-[#757575]" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-base md:text-lg text-[#757575] mb-2">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full h-12 md:h-14 px-4 pr-12 text-base md:text-lg border-2 border-gray-300 rounded-lg bg-white focus:outline-none focus:border-brand-primary"
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 min-w-[44px] min-h-[44px] flex items-center justify-center"
                >
                  {showNew ? (
                    <EyeOff className="w-5 h-5 text-[#757575]" />
                  ) : (
                    <Eye className="w-5 h-5 text-[#757575]" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-base md:text-lg text-[#757575] mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full h-12 md:h-14 px-4 pr-12 text-base md:text-lg border-2 border-gray-300 rounded-lg bg-white focus:outline-none focus:border-brand-primary"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 min-w-[44px] min-h-[44px] flex items-center justify-center"
                >
                  {showConfirm ? (
                    <EyeOff className="w-5 h-5 text-[#757575]" />
                  ) : (
                    <Eye className="w-5 h-5 text-[#757575]" />
                  )}
                </button>
              </div>
            </div>
          </div>
         <div className="w-full mt-2 p-4 bg-white rounded-lg border border-gray-200">
  <p className="text-xs font-medium text-[#757575] mb-2">
    Password must contain:
  </p>

  <ul className="space-y-1 text-xs">
    <li
      className={`flex items-center gap-2 ${
        newPassword.length >= 10 ? "text-[#4CAF50]" : "text-brand-primary"
      }`}
    >
      <span>{newPassword.length >= 10 ? "✓" : "✗"}</span>
      At least 10 characters
    </li>

    <li
      className={`flex items-center gap-2 ${
        /[a-z]/.test(newPassword)
          ? "text-[#4CAF50]"
          : "text-brand-primary"
      }`}
    >
      <span>{/[a-z]/.test(newPassword) ? "✓" : "✗"}</span>
      One lowercase letter
    </li>

    <li
      className={`flex items-center gap-2 ${
        /[A-Z]/.test(newPassword)
          ? "text-[#4CAF50]"
          : "text-brand-primary"
      }`}
    >
      <span>{/[A-Z]/.test(newPassword) ? "✓" : "✗"}</span>
      One uppercase letter
    </li>

    <li
      className={`flex items-center gap-2 ${
        /\d/.test(newPassword)
          ? "text-[#4CAF50]"
          : "text-brand-primary"
      }`}
    >
      <span>{/\d/.test(newPassword) ? "✓" : "✗"}</span>
      One number
    </li>

    <li
      className={`flex items-center gap-2 ${
        /[@$!%*?&]/.test(newPassword)
          ? "text-[#4CAF50]"
          : "text-brand-primary"
      }`}
    >
      <span>{/[@$!%*?&]/.test(newPassword) ? "✓" : "✗"}</span>
      One special character (@$!%*?&)
    </li>
  </ul>
</div>
          <div className="mt-auto pt-10 pb-4 w-full">
            <button
              type="submit"
              className="w-full bg-brand-primary hover:bg-brand-hover text-white h-12 md:h-14 lg:h-16 text-lg md:text-xl lg:text-2xl font-medium rounded-lg transition-colors"
            >
              Save Password
            </button>
          </div>
        </form>
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    </div>
  );
}